import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { CTASection } from "@/components/common/CTASection";
import { BLOG_POSTS } from "@/lib/content";
import { lineReveal } from "@/lib/motion";

export default function BlogPost() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const post = BLOG_POSTS.find((p) => p.slug === slug);
  const related = BLOG_POSTS.filter((p) => p.slug !== slug).slice(0, 2);

  if (!post) {
    return (
      <div className="container-px flex min-h-[70vh] flex-col items-center justify-center pt-[72px] text-center">
        <h1 className="font-head text-3xl font-bold text-white">Article not found</h1>
        <Link to="/resources" className="btn-primary mt-6" data-testid="back-to-resources">Back to Resources</Link>
      </div>
    );
  }

  return (
    <>
      <article className="relative pt-[72px]" data-testid="blog-post">
        {/* header */}
        <div className="relative overflow-hidden">
          <div className="absolute inset-0"><img src={post.image} alt="" className="h-full w-full object-cover opacity-20" /><div className="absolute inset-0 bg-gradient-to-b from-[#0E0E10]/70 to-[#0E0E10]" /></div>
          <div className="container-px relative py-16 lg:py-20">
            <Link to="/resources" data-testid="blog-back-link" className="mb-8 inline-flex items-center gap-2 text-sm text-zinc-400 transition-colors hover:text-orange-400">
              <ArrowLeft className="h-4 w-4" /> Back to Resources
            </Link>
            <div className="max-w-3xl">
              <span className="rounded-full border border-orange-500/20 bg-orange-500/5 px-3 py-1 font-mono text-[10px] uppercase tracking-wider text-orange-300">{post.category}</span>
              <div className="reveal-mask mt-5">
                <motion.h1 variants={lineReveal} initial="hidden" animate="show" className="font-head text-3xl font-extrabold leading-[1.1] tracking-tight text-white sm:text-4xl lg:text-5xl">
                  {post.title}
                </motion.h1>
              </div>
              <div className="mt-6 flex items-center gap-3 text-sm text-zinc-500">{post.date} · {post.readTime}</div>
            </div>
          </div>
        </div>

        {/* body */}
        <div className="container-px pb-16">
          <div className="mx-auto max-w-3xl">
            <div className="overflow-hidden rounded-[2rem] border border-white/10">
              <img src={post.image} alt={post.title} className="h-64 w-full object-cover sm:h-80" />
            </div>
            <div className="mt-10 space-y-6">
              {post.content.map((block, i) =>
                block.type === "h" ? (
                  <h2 key={i} className="font-head text-2xl font-bold text-white">{block.text}</h2>
                ) : (
                  <p key={i} className="text-base leading-[1.8] text-zinc-300">{block.text}</p>
                )
              )}
            </div>

            <div className="mt-12 rounded-2xl border border-orange-500/20 bg-[#17171A] p-8 text-center">
              <p className="font-head text-xl font-bold text-white">Ready to plan your India expansion?</p>
              <button onClick={() => navigate("/contact")} className="btn-primary mx-auto mt-6" data-testid="blog-cta">
                Book a Free Consultation <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>

        {/* related */}
        <div className="border-t border-white/10 bg-[#131316] py-16">
          <div className="container-px">
            <h3 className="mb-8 font-head text-2xl font-bold text-white">Keep reading</h3>
            <div className="grid gap-6 md:grid-cols-2">
              {related.map((p) => (
                <Link key={p.slug} to={`/resources/${p.slug}`} data-testid={`related-${p.slug}`} className="group flex gap-5 overflow-hidden rounded-2xl border border-white/8 bg-white/[0.02] p-4 transition-colors hover:border-orange-500/30">
                  <img src={p.image} alt={p.title} className="h-24 w-28 shrink-0 rounded-xl object-cover" />
                  <div>
                    <span className="font-mono text-[10px] uppercase tracking-wider text-orange-400">{p.category}</span>
                    <h4 className="mt-2 font-head text-base font-bold text-white group-hover:text-orange-400">{p.title}</h4>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </article>

      <CTASection />
    </>
  );
}
